/**
 * FVrad Dashboard - main screen
 * Flight/manual inputs, dose calculation, live cosmic monitoring, compliance export
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  TextInput,
  ScrollView,
  useColorScheme,
  Platform,
  Switch,
  Share,
  Alert,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RootStackParamList } from '../navigation/types';
import {
  estimateDoseRateMicroSvPerHour,
  estimateFlightDose,
} from '../services/radiationCalc';
import { parseItinerary } from '../services/airportService';
import { playAlertSound } from '../services/alertSoundService';
import {
  generateLogCsv,
  generateLogPdfHtml,
  saveLogToFirestore,
} from '../services/exportLogService';
import { shareCsvReport, sharePdfReport } from '../services/shareReportService';
import { writeFlightToHealth } from '../services/healthAppService';
import {
  getSimulatedHitsPerSecond,
  getSimulatedDoseRateMicroSvPerHour,
} from '../services/simulatedAviationData';
import { hitsToDoseRateMicroSvPerHour } from '../services/cameraRadiationService';
import { useExposureStore } from '../store';
import {
  DEFAULT_CRUISE_ALTITUDE_FEET,
  ICRP_ANNUAL_LIMIT_MSV,
} from '../utils/constants';
import { CameraPermissionFallback } from '../components/CameraPermissionFallback';
import { CameraRadiationView } from '../components/CameraRadiationView';
import { CircularExposureDial } from '../components/CircularExposureDial';
import { FlightRouteGraphic } from '../components/FlightRouteGraphic';
import { Logo } from '../components/Logo';
import { FlightLogCarousel } from '../components/FlightLogCarousel';
import { AnimatedCalculateButton } from '../components/AnimatedCalculateButton';
import { CrewHintOverlay } from '../components/CrewHintOverlay';
import { MinimizedDashboardView } from '../components/MinimizedDashboardView';
import { useCameraPermission } from '../hooks';
import type { ExposureLogEntry } from '../types';

type Nav = NativeStackNavigationProp<RootStackParamList>;

type InputMode = 'flight' | 'manual';

type StatusLevel = 'safe' | 'elevated' | 'alert';

const ACCENT_CYAN = '#00b8d9';
const ACCENT_CYAN_DIM = '#0097a7';
const CARD_BG_LIGHT = '#ffffff';
const CARD_BORDER_LIGHT = '#e2e8f0';
const TEXT_PRIMARY_LIGHT = '#0f172a';
const TEXT_SECONDARY_LIGHT = '#475569';
const CONTAINER_BG = '#f1f5f9';
const CARD_SHADOW = '#cbd5e1';

/** Dark mode: cyan accents on navy background */
const CONTAINER_BG_DARK = '#0f172a';
const CARD_BG_DARK = '#1e293b';
const CARD_BORDER_DARK = '#334155';
const TEXT_PRIMARY_DARK = '#f1f5f9';
const TEXT_SECONDARY_DARK = '#94a3b8';

const ICRP_LIMIT_MICRO_SV = ICRP_ANNUAL_LIMIT_MSV * 1_000_000;

/** Polar/high-latitude route indicators for geofencing alert */
const HIGH_LATITUDE_INDICATORS = [
  'JFK', 'LHR', 'OSL', 'ARN', 'CPT', 'SYD', 'AKL', 'ICN', 'NRT', 'HEL',
];

function isHighLatitudeRoute(origin: string, destination: string): boolean {
  const o = origin.trim().toUpperCase();
  const d = destination.trim().toUpperCase();
  return HIGH_LATITUDE_INDICATORS.some(
    (code) => o.includes(code) || d.includes(code)
  );
}

export function DashboardScreen() {
  const navigation = useNavigation<Nav>();
  const colorScheme = useColorScheme();
  const isDark = colorScheme === 'dark';
  const { isGranted: cameraGranted, requestPermission } = useCameraPermission();

  const [liveMonitoring, setLiveMonitoring] = useState(false);
  const [inputMode, setInputMode] = useState<InputMode>('flight');
  const [cameraAltitudeFeet, setCameraAltitudeFeet] = useState(
    String(DEFAULT_CRUISE_ALTITUDE_FEET)
  );
  const [cameraSessionMinutes, setCameraSessionMinutes] = useState('');
  const [shiftFlightHours, setShiftFlightHours] = useState('');
  const [itinerary, setItinerary] = useState('');
  const [durationMinutes, setDurationMinutes] = useState('60');
  const [exporting, setExporting] = useState(false);
  const [statusExpanded, setStatusExpanded] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [liveMonitoringExpanded, setLiveMonitoringExpanded] = useState(false);
  const [hintTrigger, setHintTrigger] = useState<
    'cruise' | 'liveMonitoring' | 'highLatitude' | null
  >(null);

  const [doseRateMicroSvPerHour, setDoseRateMicroSvPerHour] = useState<
    number | null
  >(null);
  const [flightDoseMicroSv, setFlightDoseMicroSv] = useState<number | null>(
    null
  );

  const currentDoseRate = useExposureStore(
    (s) => s.currentFlightDoseRateMicroSvPerHour
  );
  const cameraHitsPerSecond = useExposureStore((s) => s.cameraHitsPerSecond);
  useExposureStore((s) => s.logEntries.length);
  const logEntries = useExposureStore((s) => s.logEntries);
  const getAnnualCumulativeMicroSv = useExposureStore(
    (s) => s.getAnnualCumulativeMicroSv
  );
  const annualCumulative = getAnnualCumulativeMicroSv();
  const addLogEntry = useExposureStore((s) => s.addLogEntry);
  const setCurrentFlightDoseRate = useExposureStore(
    (s) => s.setCurrentFlightDoseRate
  );
  const setCameraAltitudeFeetStore = useExposureStore(
    (s) => s.setCameraAltitudeFeet
  );
  const setCameraHitsPerSecond = useExposureStore(
    (s) => s.setCameraHitsPerSecond
  );
  const calibrationFactor = useExposureStore(
    (s) => s.calibrationFactorMicroSvPerHit
  );
  const thresholdSettings = useExposureStore((s) => s.thresholdSettings);
  const isDoseRateAboveThreshold = useExposureStore(
    (s) => s.isDoseRateAboveThreshold
  );
  const isAnnualCumulativeAboveThreshold = useExposureStore(
    (s) => s.isAnnualCumulativeAboveThreshold
  );
  const healthSyncEnabled = useExposureStore((s) => s.healthSyncEnabled);
  const simulateHighEnergyHits = useExposureStore(
    (s) => s.simulateHighEnergyHits
  );

  const simIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const getStatus = useCallback((): StatusLevel => {
    const rateAbove = isDoseRateAboveThreshold();
    const annualAbove = isAnnualCumulativeAboveThreshold();
    if (rateAbove || annualAbove) return 'alert';
    const rate = currentDoseRate || doseRateMicroSvPerHour || 0;
    if (rate >= thresholdSettings.doseRateAlertMicroSvPerHour * 0.7)
      return 'elevated';
    return 'safe';
  }, [
    currentDoseRate,
    doseRateMicroSvPerHour,
    isDoseRateAboveThreshold,
    isAnnualCumulativeAboveThreshold,
    thresholdSettings.doseRateAlertMicroSvPerHour,
  ]);

  const status = getStatus();

  useEffect(() => {
    if (status === 'alert') {
      playAlertSound();
    }
  }, [status]);

  // When enabling live monitoring, request camera permission on native
  useEffect(() => {
    if (liveMonitoring && !cameraGranted && Platform.OS !== 'web') {
      requestPermission();
    }
  }, [liveMonitoring, cameraGranted, requestPermission]);

  // Show live monitoring hint when user first enables it
  useEffect(() => {
    if (liveMonitoring) {
      setHintTrigger('liveMonitoring');
    }
  }, [liveMonitoring]);

  // Web or simulate mode: use simulated aviation data when live monitoring on
  useEffect(() => {
    const useSimulated =
      liveMonitoring &&
      (Platform.OS === 'web' || simulateHighEnergyHits);
    if (!useSimulated) return;
    simIntervalRef.current = setInterval(() => {
      const hits = getSimulatedHitsPerSecond();
      const alt = parseInt(cameraAltitudeFeet, 10) || DEFAULT_CRUISE_ALTITUDE_FEET;
      const rate =
        Platform.OS === 'web' || simulateHighEnergyHits
          ? getSimulatedDoseRateMicroSvPerHour(alt)
          : hitsToDoseRateMicroSvPerHour(hits, alt);
      setCameraHitsPerSecond(hits);
      setCurrentFlightDoseRate(rate);
    }, 1000);
    return () => {
      if (simIntervalRef.current) {
        clearInterval(simIntervalRef.current);
        simIntervalRef.current = null;
      }
      if (Platform.OS === 'web' || simulateHighEnergyHits) {
        setCameraHitsPerSecond(0);
        setCurrentFlightDoseRate(0);
      }
    };
  }, [
    liveMonitoring,
    simulateHighEnergyHits,
    cameraAltitudeFeet,
    calibrationFactor,
    setCameraHitsPerSecond,
    setCurrentFlightDoseRate,
  ]);

  // Sync camera altitude to store
  useEffect(() => {
    const alt = parseInt(cameraAltitudeFeet, 10) || DEFAULT_CRUISE_ALTITUDE_FEET;
    setCameraAltitudeFeetStore(alt);
  }, [cameraAltitudeFeet, setCameraAltitudeFeetStore]);

  // Parse itinerary and auto-fill duration when valid
  const parsedItinerary = inputMode === 'flight' ? parseItinerary(itinerary) : null;
  useEffect(() => {
    if (parsedItinerary) {
      setDurationMinutes(String(parsedItinerary.durationMinutes));
    }
  }, [parsedItinerary?.durationMinutes]);

  const handleCalculate = () => {
    if (inputMode === 'flight') {
      const parsed = parseItinerary(itinerary);
      if (!parsed) return;
      const duration = parsed.durationMinutes;
      if (duration <= 0) return;
      setCalculating(true);
      setTimeout(() => {
        const input = {
          origin: parsed.originIata,
          destination: parsed.destIata,
          date: new Date().toISOString().slice(0, 10),
          durationMinutes: duration,
        };
        const dose = estimateFlightDose(input);
        const rate = duration > 0 ? dose / (duration / 60) : 0;
        setFlightDoseMicroSv(dose);
        setDoseRateMicroSvPerHour(rate);
        setCurrentFlightDoseRate(rate);
        setCalculating(false);
      }, 250);
    } else {
      const dur = parseInt(durationMinutes, 10);
      if (!durationMinutes.trim() || dur <= 0) return;
      setCalculating(true);
      setTimeout(() => {
        const alt = DEFAULT_CRUISE_ALTITUDE_FEET;
        const lat = 45;
        const rate = estimateDoseRateMicroSvPerHour(alt, lat);
        const dose = Math.round(rate * (dur / 60));
        setDoseRateMicroSvPerHour(rate);
        setFlightDoseMicroSv(dose);
        setCurrentFlightDoseRate(rate);
        setCalculating(false);
      }, 250);
    }
  };

  const handleLogFlight = async () => {
    if (flightDoseMicroSv == null || flightDoseMicroSv <= 0) return;
    const route =
      inputMode === 'flight' && parsedItinerary
        ? `${parsedItinerary.originIata}-${parsedItinerary.destIata}`
        : `Manual ${durationMinutes} min`;
    const duration = parseInt(durationMinutes, 10) || 60;
    addLogEntry({
      timestamp: new Date().toISOString(),
      route,
      doseMicroSv: flightDoseMicroSv,
      doseRateMicroSvPerHour: doseRateMicroSvPerHour ?? undefined,
    });
    if (healthSyncEnabled && Platform.OS !== 'web') {
      writeFlightToHealth({
        route,
        doseMicroSv: flightDoseMicroSv,
        durationMinutes: duration,
        timestamp: new Date().toISOString(),
      }).catch(() => {});
    }
    setFlightDoseMicroSv(null);
    setDoseRateMicroSvPerHour(null);
    setCurrentFlightDoseRate(0);
  };

  const handleLogCameraSession = async () => {
    const rate = currentDoseRate || 0;
    if (rate <= 0) return;
    const mins = parseInt(cameraSessionMinutes, 10) || 60;
    const dose = Math.round(rate * (mins / 60));
    if (dose <= 0) return;
    const alt = parseInt(cameraAltitudeFeet, 10) || DEFAULT_CRUISE_ALTITUDE_FEET;
    const route = `Camera ${alt}ft`;
    addLogEntry({
      timestamp: new Date().toISOString(),
      route,
      doseMicroSv: dose,
      doseRateMicroSvPerHour: rate,
    });
    if (healthSyncEnabled && Platform.OS !== 'web') {
      writeFlightToHealth({
        route,
        doseMicroSv: dose,
        durationMinutes: mins,
        timestamp: new Date().toISOString(),
      }).catch(() => {});
    }
    setCameraSessionMinutes('');
  };

  const handleExportLog = async (format: 'csv' | 'pdf') => {
    setExporting(true);
    const entries = logEntries.filter(
      (e) => new Date(e.timestamp).getFullYear() === new Date().getFullYear()
    ) as ExposureLogEntry[];
    const annualTotal = getAnnualCumulativeMicroSv();

    const showError = (msg: string) => Alert.alert('Export', msg);

    try {
      if (format === 'csv') {
        if (Platform.OS === 'web') {
          const csv = generateLogCsv(entries);
          const blob = new Blob([csv], { type: 'text/csv', lastModified: Date.now() });
          const url = URL.createObjectURL(blob);
          const a = (globalThis as any).document.createElement('a');
          a.href = url;
          a.download = `fvrad-exposure-${new Date().toISOString().slice(0, 10)}.csv`;
          a.click();
          URL.revokeObjectURL(url);
        } else {
          await shareCsvReport(entries, showError);
        }
      } else {
        if (Platform.OS === 'web') {
          const html = generateLogPdfHtml(entries, annualTotal);
          const w = (globalThis as any).window.open('', '_blank');
          if (w) {
            w.document.write(html);
            w.document.close();
            w.print();
          }
        } else {
          await sharePdfReport(entries, annualTotal, showError);
        }
      }
      await saveLogToFirestore(entries, annualTotal);
    } catch (err) {
      Alert.alert('Export', 'Export failed. Try again.');
    } finally {
      setExporting(false);
    }
  };

  const highLatitudeAlert =
    inputMode === 'flight' &&
    parsedItinerary != null &&
    isHighLatitudeRoute(parsedItinerary.originIata, parsedItinerary.destIata);

  // Show high-latitude hint when user enters polar route
  useEffect(() => {
    if (highLatitudeAlert) {
      setHintTrigger('highLatitude');
    }
  }, [highLatitudeAlert]);

  const shiftDoseMicroSv =
    shiftFlightHours && currentDoseRate
      ? Math.round(
          currentDoseRate * parseFloat(shiftFlightHours || '0')
        )
      : null;

  const annualProgress = Math.min(
    100,
    (annualCumulative / ICRP_LIMIT_MICRO_SV) * 100
  );
  const isIcrpWarning = annualCumulative >= ICRP_LIMIT_MICRO_SV * 0.8;

  const statusColors = isDark
    ? {
        safe: { bg: '#14532d', border: '#22c55e', text: '#86efac' },
        elevated: { bg: '#713f12', border: '#eab308', text: '#fde047' },
        alert: { bg: '#7f1d1d', border: '#ef4444', text: '#fca5a5' },
      }
    : {
        safe: { bg: '#dcfce7', border: '#22c55e', text: '#15803d' },
        elevated: { bg: '#fef9c3', border: '#eab308', text: '#a16207' },
        alert: { bg: '#fee2e2', border: '#ef4444', text: '#b91c1c' },
      };
  const sc = statusColors[status];

  const theme = isDark
    ? {
        card: CARD_BG_DARK,
        border: CARD_BORDER_DARK,
        text: TEXT_PRIMARY_DARK,
        muted: TEXT_SECONDARY_DARK,
      }
    : {
        card: CARD_BG_LIGHT,
        border: CARD_BORDER_LIGHT,
        text: TEXT_PRIMARY_LIGHT,
        muted: TEXT_SECONDARY_LIGHT,
      };

  const containerBg = isDark ? CONTAINER_BG_DARK : CONTAINER_BG;
  const containerStyle = [
    styles.container,
    { backgroundColor: containerBg },
    Platform.OS === 'web' && ({ minHeight: '100%' } as any),
  ];

  // Minimized view: compact dose dial + alert for in-flight monitoring
  if (minimized) {
    return (
      <SafeAreaView style={containerStyle} edges={['top']}>
        <MinimizedDashboardView
          annualCumulativeMicroSv={annualCumulative}
          isAlert={status === 'alert'}
          currentDoseRateMicroSvPerHour={currentDoseRate ?? doseRateMicroSvPerHour}
          onExpand={() => setMinimized(false)}
          fallback={Platform.OS === 'web'}
          dark={isDark}
        />
        <CrewHintOverlay triggerHint={hintTrigger} onDismiss={() => setHintTrigger(null)} />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={containerStyle} edges={['top']} testID="dashboard-screen">
      <CrewHintOverlay triggerHint={hintTrigger} onDismiss={() => setHintTrigger(null)} />
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={[
          styles.scrollContent,
          Platform.OS === 'web' && { maxWidth: 480, alignSelf: 'center' as const, width: '100%' },
        ]}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.headerRow}>
          <Logo width={120} height={36} dark={isDark} />
          <Pressable
            style={[styles.minimizeButton, { borderColor: theme.border }]}
            onPress={() => setMinimized(true)}
            accessibilityLabel="Minimize for in-flight monitoring"
          >
            <Text style={[styles.minimizeButtonText, { color: theme.muted }]}>
              Minimize
            </Text>
          </Pressable>
        </View>

        {/* Status indicator - tap to expand dose breakdown */}
        <Pressable
          style={[styles.statusCard, { backgroundColor: sc.bg, borderColor: sc.border }]}
          onPress={() => setStatusExpanded((e) => !e)}
          accessibilityRole="button"
          accessibilityLabel={`Exposure status: ${status}. ${statusExpanded ? 'Expanded' : 'Tap to expand'} dose breakdown`}
        >
          <View style={styles.statusRow}>
            <View>
              <Text style={[styles.statusLabel, { color: theme.muted }]}>Status</Text>
              <Text style={[styles.statusValue, { color: sc.text }]}>
                {status === 'safe' && 'Safe'}
                {status === 'elevated' && 'Elevated'}
                {status === 'alert' && 'Alert'}
              </Text>
            </View>
            <Text style={[styles.statusExpandHint, { color: theme.muted }]}>
              {statusExpanded ? '▼' : '▶'} Tap for details
            </Text>
          </View>
          {statusExpanded && (
            <View style={[styles.statusBreakdown, { borderTopColor: sc.border }]}>
              <Text style={[styles.breakdownLabel, { color: theme.muted }]}>
                Dose breakdown (YTD)
              </Text>
              <Text style={[styles.breakdownRow, { color: theme.text }]}>
                Cosmic (flights): {(annualCumulative / 1_000_000).toFixed(2)} mSv
              </Text>
              <Text style={[styles.breakdownRow, { color: theme.muted }]}>
                Background (est.): ~2.4 mSv/year
              </Text>
              <Text style={[styles.breakdownRow, { color: theme.text }]}>
                Current rate: {(currentDoseRate ?? doseRateMicroSvPerHour ?? 0).toFixed(2)} µSv/h
              </Text>
            </View>
          )}
        </Pressable>

        {/* Live Cosmic Dose Monitoring - collapsible, camera optional */}
        <View style={[styles.card, styles.cardCompact, { backgroundColor: theme.card, borderColor: theme.border }]}>
          <Pressable
            style={styles.toggleRow}
            onPress={() => setLiveMonitoringExpanded((e) => !e)}
          >
            <View style={styles.toggleLabelBlock}>
              <Text style={[styles.cardLabel, { color: theme.muted }]}>
                Live cosmic monitoring
              </Text>
              <Text style={[styles.cardUnit, { color: theme.muted }]}>
                {liveMonitoring ? 'On' : 'Off'} • In-flight: cover lens
              </Text>
            </View>
            <Switch
              value={liveMonitoring}
              onValueChange={(v) => {
                setLiveMonitoring(v);
                if (v) setLiveMonitoringExpanded(true);
              }}
              trackColor={{ false: theme.border, true: ACCENT_CYAN_DIM }}
              thumbColor={liveMonitoring ? ACCENT_CYAN : '#94a3b8'}
            />
          </Pressable>

          {liveMonitoring && liveMonitoringExpanded && (
            <>
              {Platform.OS === 'web' ? (
                <View style={[styles.simBanner, { backgroundColor: theme.card, borderColor: theme.border }]}>
                  <Text style={[styles.simBannerText, { color: theme.muted }]}>
                    Web: Simulated aviation data
                  </Text>
                </View>
              ) : simulateHighEnergyHits ? (
                <View style={[styles.simBanner, { backgroundColor: '#e0f7fa', borderColor: ACCENT_CYAN }]}>
                  <Text style={[styles.simBannerText, { color: '#00796b' }]}>
                    Test mode: Simulated hits (Settings → Test filter)
                  </Text>
                </View>
              ) : !cameraGranted ? (
                <CameraPermissionFallback />
              ) : (
                <View style={[styles.lightTightBanner, { backgroundColor: '#e0f7fa', borderColor: ACCENT_CYAN }]}>
                  <Text style={styles.lightTightText}>
                    Cover lens during flight • High-energy filter active
                  </Text>
                </View>
              )}

              {Platform.OS !== 'web' && !simulateHighEnergyHits && cameraGranted && (
                <View style={styles.cameraCompact}>
                  <CameraRadiationView />
                </View>
              )}

              <View style={styles.shiftRow}>
                <TextInput
                  style={[styles.input, styles.inputHalf, styles.inputCompact, { color: theme.text, borderColor: theme.border }]}
                  placeholder="Flight hrs"
                  placeholderTextColor={theme.muted}
                  value={shiftFlightHours}
                  onChangeText={setShiftFlightHours}
                  keyboardType="decimal-pad"
                />
                <TextInput
                  style={[styles.input, styles.inputHalf, styles.inputCompact, { color: theme.text, borderColor: theme.border }]}
                  placeholder="Altitude (ft)"
                  placeholderTextColor={theme.muted}
                  value={cameraAltitudeFeet}
                  onChangeText={setCameraAltitudeFeet}
                  keyboardType="numeric"
                />
              </View>

              <View style={styles.metricsGrid}>
                <View style={[styles.metricBox, { backgroundColor: '#e0f7fa', borderColor: ACCENT_CYAN }]}>
                  <Text style={[styles.metricLabel, { color: theme.muted }]}>Hits/sec</Text>
                  <Text style={[styles.metricValue, { color: ACCENT_CYAN }]}>
                    {(cameraHitsPerSecond || 0).toFixed(1)}
                  </Text>
                </View>
                <View style={[styles.metricBox, { backgroundColor: '#e0f7fa', borderColor: ACCENT_CYAN }]}>
                  <Text style={[styles.metricLabel, { color: theme.muted }]}>µSv/h</Text>
                  <Text style={[styles.metricValue, { color: ACCENT_CYAN }]}>
                    {(currentDoseRate || 0).toFixed(2)}
                  </Text>
                </View>
              </View>

              {shiftDoseMicroSv != null && shiftDoseMicroSv > 0 && (
                <Text style={[styles.cardUnit, { color: theme.muted }]}>
                  Shift dose: {(shiftDoseMicroSv / 1_000_000).toFixed(2)} mSv
                </Text>
              )}

              <View style={styles.sessionRow}>
                <TextInput
                  style={[styles.input, styles.inputCompact, { flex: 1, color: theme.text, borderColor: theme.border }]}
                  placeholder="Session (min) to log"
                  placeholderTextColor={theme.muted}
                  value={cameraSessionMinutes}
                  onChangeText={setCameraSessionMinutes}
                  keyboardType="numeric"
                />
                <Pressable
                  style={[styles.secondaryButton, styles.secondaryButtonCompact, { borderColor: ACCENT_CYAN }]}
                  onPress={handleLogCameraSession}
                >
                  <Text style={[styles.secondaryButtonText, { color: ACCENT_CYAN }]}>
                    Log
                  </Text>
                </Pressable>
              </View>
            </>
          )}
        </View>

        {/* Cumulative exposure - bucket style: X% of 6 mSv limit */}
        <View style={[styles.card, styles.cardCompact, { backgroundColor: theme.card, borderColor: theme.border }]}>
          <Text style={[styles.cardLabel, { color: theme.muted }]}>
            Annual dose (YTD)
          </Text>
          <View style={styles.dialRow}>
            <CircularExposureDial
              annualCumulativeMicroSv={annualCumulative}
              limitMicroSv={ICRP_LIMIT_MICRO_SV}
              size={88}
              fallback={Platform.OS === 'web'}
              dark={isDark}
            />
            <View style={styles.dialLegend}>
              <Text style={[styles.dialLegendMain, { color: theme.text }]}>
                {(annualCumulative / 1_000_000).toFixed(1)} of {ICRP_ANNUAL_LIMIT_MSV} mSv
              </Text>
              <Text style={[styles.cardUnit, { color: theme.muted }]}>
                Occupational limit
              </Text>
              <View style={[styles.progressTrack, { backgroundColor: theme.border, marginTop: 6 }]}>
                <View
                  style={[
                    styles.progressBar,
                    {
                      width: `${annualProgress}%`,
                      backgroundColor: isIcrpWarning ? '#f87171' : ACCENT_CYAN,
                    },
                  ]}
                />
              </View>
            </View>
          </View>
          {isIcrpWarning && (
            <Text style={[styles.icrpWarningInline, { color: '#f87171' }]}>
              ⚠ Approaching limit
            </Text>
          )}
        </View>

        {/* Swipeable flight log carousel */}
        <FlightLogCarousel
          entries={logEntries}
          onEntryPress={() => navigation.navigate('DoseLog')}
          dark={isDark}
        />

        {/* Geofencing: high-latitude route alert */}
        {highLatitudeAlert && (
          <View style={[styles.geoAlert, { backgroundColor: '#3d3008', borderColor: '#6b5a0a' }]}>
            <Text style={[styles.geoAlertText, { color: '#facc15' }]}>
              High-latitude route — elevated cosmic radiation expected
            </Text>
          </View>
        )}

        {/* Input mode toggle */}
        <View style={styles.modeRow}>
          <Pressable
            style={[
              styles.modeButton,
              { backgroundColor: theme.card, borderColor: theme.border },
              inputMode === 'flight' && {
                borderColor: ACCENT_CYAN,
                backgroundColor: '#e0f7fa',
              } as const,
            ]}
            onPress={() => setInputMode('flight')}
          >
            <Text
              style={[
                styles.modeButtonText,
                { color: theme.muted },
                inputMode === 'flight' && { color: ACCENT_CYAN },
              ]}
            >
              Flight route
            </Text>
          </Pressable>
          <Pressable
            style={[
              styles.modeButton,
              { backgroundColor: theme.card, borderColor: theme.border },
              inputMode === 'manual' && {
                borderColor: ACCENT_CYAN,
                backgroundColor: '#e0f7fa',
              } as const,
            ]}
            onPress={() => {
              setInputMode('manual');
              setDurationMinutes('');
            }}
          >
            <Text
              style={[
                styles.modeButtonText,
                { color: theme.muted },
                inputMode === 'manual' && { color: ACCENT_CYAN },
              ]}
            >
              Manual
            </Text>
          </Pressable>
        </View>

        {/* Form inputs */}
        <View style={[styles.card, { backgroundColor: theme.card, borderColor: theme.border }]}>
          {inputMode === 'flight' ? (
            <>
              <FlightRouteGraphic
                origin={parsedItinerary?.originIata ?? ''}
                destination={parsedItinerary?.destIata ?? ''}
                visible={flightDoseMicroSv != null}
              />
              <TextInput
                style={[styles.input, { color: theme.text, borderColor: theme.border }]}
                placeholder="Flight itinerary (e.g. JFK to LHR, New York to London)"
                placeholderTextColor={theme.muted}
                value={itinerary}
                onChangeText={setItinerary}
              />
              <Text style={[styles.inputHint, { color: theme.muted }]}>
                Origin and destination — city names or airport codes. Duration auto-calculated.
              </Text>
              {parsedItinerary && (
                <Text style={[styles.inputHint, { color: ACCENT_CYAN }]}>
                  {parsedItinerary.originIata} → {parsedItinerary.destIata} • {parsedItinerary.durationMinutes} min
                </Text>
              )}
            </>
          ) : (
            <>
              <TextInput
                style={[styles.input, { color: theme.text, borderColor: theme.border }]}
                placeholder="Flight duration (minutes)"
                placeholderTextColor={theme.muted}
                value={durationMinutes}
                onChangeText={setDurationMinutes}
                keyboardType="numeric"
              />
              <Text style={[styles.inputHint, { color: theme.muted }]}>
                Manual mode: enter duration for dose estimate
              </Text>
            </>
          )}
          <AnimatedCalculateButton
            onPress={handleCalculate}
            loading={calculating}
            reduceMotion={liveMonitoring}
            disabled={inputMode === 'flight' && !parsedItinerary}
          />
        </View>

        {/* Results */}
        {(doseRateMicroSvPerHour != null || flightDoseMicroSv != null) && (
          <View style={[styles.card, { backgroundColor: theme.card, borderColor: theme.border }]}>
            <Text style={[styles.cardLabel, { color: theme.muted }]}>
              Estimated dose rate
            </Text>
            <Text style={[styles.cardValue, { color: ACCENT_CYAN }]}>
              {(doseRateMicroSvPerHour ?? 0).toFixed(2)} µSv/h
            </Text>
            <Text style={[styles.cardLabel, { color: theme.muted }]}>
              Total flight dose
            </Text>
            <Text style={[styles.cardValue, { color: ACCENT_CYAN }]}>
              {(flightDoseMicroSv ?? 0).toLocaleString()} µSv
            </Text>
            <Text style={[styles.cardUnit, { color: theme.muted }]}>
              {((flightDoseMicroSv ?? 0) / 1_000_000).toFixed(2)} mSv
            </Text>
            <Pressable
              style={[styles.secondaryButton, { borderColor: ACCENT_CYAN }]}
              onPress={handleLogFlight}
            >
              <Text style={[styles.secondaryButtonText, { color: ACCENT_CYAN }]}>
                Log flight
              </Text>
            </Pressable>
          </View>
        )}

        {/* Share Report - single PDF button */}
        <View style={[styles.shareRow, { backgroundColor: theme.card, borderColor: theme.border }]}>
          <Pressable
            style={[styles.shareButton, { borderColor: ACCENT_CYAN }]}
            onPress={() => handleExportLog('pdf')}
            disabled={exporting}
          >
            <Text style={styles.shareIcon}>↗</Text>
            <Text style={[styles.shareButtonText, { color: ACCENT_CYAN }]}>
              Share PDF
            </Text>
          </Pressable>
        </View>
      </ScrollView>

      {/* Always-visible nav bar - icon style */}
      <View style={[styles.navBar, { backgroundColor: theme.card, borderTopColor: theme.border }]} testID="nav-row">
        <Pressable
          testID="nav-tips"
          accessibilityRole="button"
          accessibilityLabel="Tips"
          style={styles.navIconButton}
          onPress={() => navigation.navigate('Tips')}
        >
          <Text style={styles.navIcon}>💡</Text>
          <Text style={[styles.navIconLabel, { color: theme.muted }]}>Tips</Text>
        </Pressable>
        <Pressable
          testID="nav-history"
          accessibilityRole="button"
          accessibilityLabel="History"
          style={styles.navIconButton}
          onPress={() => navigation.navigate('DoseLog')}
        >
          <Text style={styles.navIcon}>📋</Text>
          <Text style={[styles.navIconLabel, { color: theme.muted }]}>History</Text>
        </Pressable>
        <Pressable
          testID="nav-settings"
          accessibilityRole="button"
          accessibilityLabel="Settings"
          style={styles.navIconButton}
          onPress={() => navigation.navigate('Settings')}
        >
          <Text style={styles.navIcon}>⚙</Text>
          <Text style={[styles.navIconLabel, { color: theme.muted }]}>Settings</Text>
        </Pressable>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  scroll: { flex: 1 },
  scrollContent: { padding: 16, paddingBottom: 48, flexGrow: 1 },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  minimizeButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
  },
  minimizeButtonText: { fontSize: 12, fontWeight: '600' },
  statusCard: {
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 12,
  },
  statusRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  statusLabel: { fontSize: 12, marginBottom: 4 },
  statusValue: { fontSize: 22, fontWeight: '700' },
  statusExpandHint: { fontSize: 11, marginTop: 4 },
  statusBreakdown: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
  },
  breakdownLabel: { fontSize: 12, marginBottom: 8, fontWeight: '600' },
  breakdownRow: { fontSize: 13, marginBottom: 4 },
  dialRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 16,
    marginTop: 8,
  },
  dialLegend: { flex: 1, minWidth: 100 },
  dialLegendMain: { fontSize: 14, fontWeight: '600' },
  inputHint: {
    fontSize: 11,
    marginTop: -8,
    marginBottom: 8,
  },
  card: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 16,
    ...(Platform.OS !== 'web' && {
      shadowColor: CARD_SHADOW,
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.08,
      shadowRadius: 8,
      elevation: 2,
    }),
  },
  cardCompact: {
    padding: 12,
    marginBottom: 12,
  },
  cardLabel: { fontSize: 12, marginBottom: 4 },
  cardValue: { fontSize: 24, fontWeight: '700' },
  cardUnit: { fontSize: 12, marginTop: 4 },
  toggleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  toggleLabelBlock: { flex: 1 },
  lightTightBanner: {
    padding: 10,
    borderRadius: 8,
    borderWidth: 1,
    marginBottom: 12,
    alignItems: 'center',
  },
  lightTightText: {
    color: '#00796b',
    fontWeight: '600',
    fontSize: 14,
  },
  simBanner: {
    padding: 10,
    borderRadius: 8,
    borderWidth: 1,
    marginBottom: 12,
  },
  simBannerText: { fontSize: 13 },
  shiftRow: { flexDirection: 'row', gap: 8 },
  inputHalf: { flex: 1 },
  inputCompact: { padding: 10, marginBottom: 8, fontSize: 14 },
  cameraCompact: { marginBottom: 8, borderRadius: 8, overflow: 'hidden' },
  sessionRow: { flexDirection: 'row', gap: 8, alignItems: 'center', marginTop: 4 },
  secondaryButtonCompact: { padding: 10, marginTop: 0 },
  metricsGrid: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 12,
  },
  metricBox: {
    flex: 1,
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
  },
  metricLabel: { fontSize: 11, marginBottom: 4 },
  metricValue: { fontSize: 20, fontWeight: '700' },
  progressTrack: {
    height: 8,
    borderRadius: 4,
    marginTop: 12,
    overflow: 'hidden',
  },
  progressBar: {
    height: '100%',
    borderRadius: 4,
  },
  icrpWarning: {
    padding: 10,
    borderRadius: 8,
    borderWidth: 1,
    marginTop: 8,
  },
  icrpWarningText: { color: '#f87171', fontWeight: '600', fontSize: 13 },
  icrpWarningInline: { fontSize: 11, fontWeight: '600', marginTop: 4 },
  geoAlert: {
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    marginBottom: 16,
  },
  geoAlertText: { fontSize: 13 },
  modeRow: { flexDirection: 'row', gap: 8, marginBottom: 12 },
  modeButton: {
    flex: 1,
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
    borderWidth: 1,
  },
  modeButtonText: { fontWeight: '600' },
  input: {
    borderWidth: 1,
    padding: 12,
    borderRadius: 8,
    marginBottom: 12,
    fontSize: 16,
  },
  primaryButton: {
    backgroundColor: ACCENT_CYAN,
    padding: 16,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 4,
  },
  primaryButtonLight: { backgroundColor: ACCENT_CYAN_DIM },
  primaryButtonText: { color: '#ffffff', fontWeight: '700', fontSize: 16 },
  secondaryButton: {
    borderWidth: 2,
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 12,
  },
  secondaryButtonText: { fontWeight: '600' },
  exportRow: { flexDirection: 'row', gap: 12, marginTop: 12 },
  exportButton: {
    flex: 1,
    borderWidth: 2,
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  exportButtonText: { fontWeight: '600' },
  shareRow: {
    flexDirection: 'row',
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 12,
  },
  shareButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    borderWidth: 2,
    padding: 12,
    borderRadius: 8,
  },
  shareIcon: { fontSize: 18, fontWeight: '700' },
  shareButtonText: { fontWeight: '600' },
  navBar: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
    paddingVertical: 8,
    paddingBottom: Platform.OS === 'ios' ? 24 : 12,
    borderTopWidth: 1,
  },
  navIconButton: {
    alignItems: 'center',
    paddingVertical: 4,
    paddingHorizontal: 16,
  },
  navIcon: { fontSize: 22, marginBottom: 2 },
  navIconLabel: { fontSize: 10, fontWeight: '500' },
  navRow: { flexDirection: 'row', gap: 12, marginTop: 8 },
  navButton: {
    flex: 1,
    padding: 14,
    borderRadius: 8,
    alignItems: 'center',
    borderWidth: 1,
  },
  navButtonText: { fontWeight: '600' },
});
